import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { jwtDecode } from 'jwt-decode'

interface User {
  id: string
  role: string
  email?: string
  phoneNumber?: string
  firstName?: string
  lastName?: string
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  token: string | null
  login: (token: string) => void
  logout: () => void
  checkAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isLoading: true,
      user: null,
      token: null,

      login: (token: string) => {
        try {
          const decoded = jwtDecode(token) as any
          const user: User = {
            id: decoded.id,
            role: decoded.role,
            email: decoded.email,
            phoneNumber: decoded.phoneNumber,
            firstName: decoded.firstName,
            lastName: decoded.lastName,
          }

          localStorage.setItem('auth_token', token)
          set({
            isAuthenticated: true,
            user,
            token,
            isLoading: false,
          })
        } catch (error) {
          console.error('Invalid token:', error)
          get().logout()
        }
      },

      logout: () => {
        localStorage.removeItem('auth_token')
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
        })
      },

      checkAuth: () => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          try {
            const decoded = jwtDecode(token) as any
            const now = Date.now() / 1000

            if (decoded.exp && decoded.exp < now) {
              // Token expired
              get().logout()
            } else {
              const user: User = {
                id: decoded.id,
                role: decoded.role,
                email: decoded.email,
                phoneNumber: decoded.phoneNumber,
                firstName: decoded.firstName,
                lastName: decoded.lastName,
              }

              set({
                isAuthenticated: true,
                user,
                token,
                isLoading: false,
              })
            }
          } catch (error) {
            console.error('Invalid token:', error)
            get().logout()
          }
        } else {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
      }),
    }
  )
)
